import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Layout, Header } from '../components/Layout';
import { getQuote, updateQuote, convertQuoteToOrder, formatPhoneE164, generateInvoiceFromQuote, generateAgreementFromQuote } from '../lib/api';
import api from '../lib/api';
import { PricingEditor } from '../components/PricingEditor';
import USARouteMap from '../components/USARouteMap';
import TransportFacts from '../components/TransportFacts';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { ArrowLeft, Save, ArrowRightCircle, Car, ExternalLink, Map, FileText, ScrollText, Send } from 'lucide-react';
import { toast } from 'sonner';

const VEHICLE_BODY_TYPES = [
  { group: 'Sedan', options: ['Small Sedan', 'Medium Sedan', 'Large Sedan'] },
  { group: 'SUV', options: ['Small SUV', 'Medium SUV', 'Large SUV'] },
  { group: 'Truck', options: ['Pickup Truck', 'Box Truck', 'Flatbed Truck'] },
  { group: 'Van', options: ['Minivan', 'Cargo Van', 'Passenger Van'] },
  { group: 'Coupe / Sports', options: ['Coupe', 'Convertible', 'Sports Car'] },
  { group: 'Wagon / Hatch', options: ['Hatchback', 'Station Wagon'] },
  { group: 'Specialty', options: ['Motorcycle', 'ATV/UTV', 'RV/Motorhome'] },
  { group: 'Boat', options: ['Small Boat', 'Medium Boat', 'Large Boat'] },
  { group: 'Trailer', options: ['Small Trailer', 'Large Trailer', 'Enclosed Trailer'] },
  { group: 'Bus', options: ['Mini Bus', 'Full Size Bus'] },
  { group: 'Other', options: ['Other'] },
];

const LEAD_STATUSES = [
  { value: 'new', label: 'New' },
  { value: 'quoted', label: 'Quoted' },
  { value: 'follow_up_needed', label: 'Follow-up Needed' },
  { value: 'ai_calling', label: 'AI Calling' },
  { value: 'no_answer', label: 'No Answer' },
  { value: 'voicemail_left', label: 'Voicemail Left' },
  { value: 'interested', label: 'Interested' },
  { value: 'callback_scheduled', label: 'Callback Scheduled' },
  { value: 'not_interested', label: 'Not Interested' },
  { value: 'do_not_call', label: 'Do Not Call' },
  { value: 'booked', label: 'Booked' },
  { value: 'lost', label: 'Lost' },
  { value: 'wrong_number', label: 'Wrong Number' },
  { value: 'lead', label: 'Lead (Legacy)' },
  { value: 'order', label: 'Order' },
  { value: 'dispatched', label: 'Dispatched' },
  { value: 'delivered', label: 'Delivered' },
  { value: 'cancelled', label: 'Cancelled' },
];

const DEFAULT_PRICING = {
  standard: { deposit_fee: 150, carrier_fee: 400, total_price: 550 },
  expedited: { deposit_fee: 175, carrier_fee: 600, total_price: 775 },
  enclosed: { deposit_fee: 200, carrier_fee: 800, total_price: 1000 },
};

const QuoteDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [pricing, setPricing] = useState({ ...DEFAULT_PRICING });
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [showConvertConfirm, setShowConvertConfirm] = useState(false);
  const [generatingInvoice, setGeneratingInvoice] = useState(false);
  const isNew = !id || id === 'new';

  useEffect(() => {
    if (isNew) {
      setForm({ customer_name: '', phone: '', email: '', agent_name: '', vehicle_year: '', vehicle_make: '', vehicle_model: '',
        pickup_city: '', pickup_state: '', pickup_zip: '', delivery_city: '', delivery_state: '', delivery_zip: '',
        pickup_address: '', delivery_address: '',
        shipping_type: 'standard', price: 0, deposit_fee: 150, carrier_fee: 0, status: 'new', notes: '', source: '' });
      setLoading(false);
      return;
    }
    const fetch = async () => {
      try {
        const res = await getQuote(id);
        setQuote(res.data);
        setForm(res.data);
        setPricing({
          standard: res.data.pricing_standard || DEFAULT_PRICING.standard,
          expedited: res.data.pricing_expedited || DEFAULT_PRICING.expedited,
          enclosed: res.data.pricing_enclosed || DEFAULT_PRICING.enclosed,
        });
      } catch { toast.error('Failed to load quote'); navigate('/quotes'); }
      finally { setLoading(false); }
    };
    fetch();
  }, [id, isNew, navigate]);

  const fetchNotes = useCallback(async () => {
    if (isNew) return;
    try { const res = await api.get(`/quotes/${id}/notes`); setNotes(res.data); } catch {}
  }, [id, isNew]);
  useEffect(() => { fetchNotes(); }, [fetchNotes]);

  const handleAddNote = async () => {
    if (!newNote.trim()) return;
    try {
      const res = await api.post(`/quotes/${id}/notes`, { text: newNote.trim() });
      setNotes(prev => [...prev, res.data]);
      setNewNote('');
      toast.success('Note added');
    } catch { toast.error('Failed to add note'); }
  };

  const handlePricingChange = (updated) => {
    setPricing(updated);
    // Also update the primary price to standard
    if (updated.standard) {
      setForm(f => ({
        ...f,
        price: updated.standard.total_price,
        deposit_fee: updated.standard.deposit_fee,
        carrier_fee: updated.standard.carrier_fee,
      }));
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const saveData = {
        ...form,
        pricing_standard: pricing.standard,
        pricing_expedited: pricing.expedited,
        pricing_enclosed: pricing.enclosed,
      };
      if (isNew) {
        const { createQuote } = await import('../lib/api');
        const res = await createQuote(saveData);
        toast.success(`Quote ${res.data.quote_number} created!`);
        navigate(`/quotes/${res.data.id}`);
      } else {
        const res = await updateQuote(id, saveData);
        setQuote(res.data);
        setForm(res.data);
        toast.success('Quote updated');
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to save'); }
    finally { setSaving(false); }
  };

  const handleConvert = async () => {
    setShowConvertConfirm(true);
  };

  const doConvert = async () => {
    setShowConvertConfirm(false);
    try {
      const res = await convertQuoteToOrder(id);
      toast.success(`Converted to order ${res.data.order_number}! Review and edit all fields below.`);
      navigate(`/orders/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to convert'); }
  };

  const handleGenerateInvoice = async (invoiceType) => {
    setGeneratingInvoice(true);
    try {
      const res = await generateInvoiceFromQuote(id, invoiceType);
      toast.success(`${invoiceType === 'customer' ? 'Customer' : 'Carrier'} invoice created: ${res.data.invoice_number}`);
      navigate(`/invoices/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate invoice'); }
    finally { setGeneratingInvoice(false); }
  };

  const handleGenerateAgreement = async () => {
    setGeneratingInvoice(true);
    try {
      const res = await generateAgreementFromQuote(id);
      toast.success(`Agreement created: ${res.data.agreement_number}`);
      navigate(`/agreements/${res.data.id}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to generate agreement'); }
    finally { setGeneratingInvoice(false); }
  };

  const vehicleSearch = `${form.vehicle_year} ${form.vehicle_make} ${form.vehicle_model}`.trim();

  if (loading) return <Layout><div className="flex items-center justify-center h-[80vh]"><div className="animate-spin w-10 h-10 border-4 border-blue-600 border-t-transparent rounded-full" /></div></Layout>;

  return (
    <Layout>
      <Header title={isNew ? 'New Quote' : `Quote ${quote?.quote_number || ''}`}>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/quotes')}><ArrowLeft className="w-4 h-4 mr-2" />Back</Button>
          {!isNew && quote?.status !== 'order' && quote?.status !== 'delivered' && (
            <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConvert} data-testid="convert-to-order-btn">
              <ArrowRightCircle className="w-4 h-4 mr-2" />Convert to Order
            </Button>
          )}
          <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={handleSave} disabled={saving} data-testid="save-quote-btn">
            <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
          </Button>
        </div>
      </Header>
      <div className="p-6 max-w-5xl" data-testid="quote-detail-page">
        <div className="space-y-6">
          {/* Quick Actions */}
          {!isNew && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quote-actions-section">
              <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2"><Send className="w-4 h-4 text-blue-600" />Actions</h3>
              <div className="flex flex-wrap gap-3">
                {quote?.status !== 'order' && quote?.status !== 'delivered' && (
                  <Button className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={handleConvert} disabled={generatingInvoice} data-testid="action-convert-order">
                    <ArrowRightCircle className="w-4 h-4 mr-2" />Convert to Order
                  </Button>
                )}
                <Button className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => handleGenerateInvoice('customer')} disabled={generatingInvoice} data-testid="action-customer-invoice">
                  <FileText className="w-4 h-4 mr-2" />Customer Invoice
                </Button>
                <Button className="bg-indigo-600 hover:bg-indigo-700 text-white" onClick={() => handleGenerateInvoice('carrier')} disabled={generatingInvoice} data-testid="action-carrier-invoice">
                  <FileText className="w-4 h-4 mr-2" />Carrier Invoice
                </Button>
                <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleGenerateAgreement} disabled={generatingInvoice} data-testid="action-agreement">
                  <ScrollText className="w-4 h-4 mr-2" />Transport Agreement
                </Button>
              </div>
            </div>
          )}

          {/* Customer Info */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Customer Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div><Label>Customer Name *</Label><Input value={form.customer_name || ''} onChange={e => setForm(f => ({...f, customer_name: e.target.value}))} data-testid="input-customer-name" /></div>
              <div>
                <Label>Phone</Label>
                <div className="relative">
                  <Input value={form.phone || ''} onChange={e => setForm(f => ({...f, phone: e.target.value}))}
                    onBlur={e => { const { formatted } = formatPhoneE164(e.target.value); if (formatted) setForm(f => ({...f, phone: formatted})); }}
                    data-testid="input-phone" />
                  {form.phone && !formatPhoneE164(form.phone).valid && (
                    <span className="absolute right-2 top-2.5 text-red-500 text-xs" title="Invalid phone format">!</span>
                  )}
                </div>
              </div>
              <div><Label>Email</Label><Input value={form.email || ''} onChange={e => setForm(f => ({...f, email: e.target.value}))} data-testid="input-email" /></div>
              <div><Label>Agent Name</Label><Input value={form.agent_name || ''} onChange={e => setForm(f => ({...f, agent_name: e.target.value}))} data-testid="input-agent" /></div>
              <div><Label>Source</Label><Input value={form.source || ''} onChange={e => setForm(f => ({...f, source: e.target.value}))} /></div>
              <div><Label>Pickup Date</Label><Input type="date" value={form.pickup_date || ''} onChange={e => setForm(f => ({...f, pickup_date: e.target.value}))} data-testid="input-pickup-date" /></div>
              <div>
                <Label>Status</Label>
                <Select value={form.status || 'new'} onValueChange={v => setForm(f => ({...f, status: v}))}>
                  <SelectTrigger data-testid="select-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{LEAD_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            {quote && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                <div className="text-sm">
                  <span className="text-slate-400">Created: </span>
                  <span className="text-slate-600 font-medium">{quote.created_at ? new Date(quote.created_at).toLocaleString() : '-'}</span>
                </div>
                <div className="text-sm">
                  <span className="text-slate-400">Last Updated: </span>
                  <span className="text-slate-600 font-medium">{quote.updated_at ? new Date(quote.updated_at).toLocaleString() : '-'}</span>
                </div>
              </div>
            )}
          </div>

          {/* Vehicle */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Vehicle Information</h3>
              {vehicleSearch && (
                <Button variant="outline" size="sm" onClick={() => window.open(`https://www.google.com/search?tbm=isch&q=${encodeURIComponent(vehicleSearch)}`, '_blank')} data-testid="view-vehicle-btn">
                  <Car className="w-4 h-4 mr-1" />View Vehicle <ExternalLink className="w-3 h-3 ml-1" />
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div><Label>Year</Label><Input value={form.vehicle_year || ''} onChange={e => setForm(f => ({...f, vehicle_year: e.target.value}))} data-testid="input-year" /></div>
              <div><Label>Make</Label><Input value={form.vehicle_make || ''} onChange={e => setForm(f => ({...f, vehicle_make: e.target.value}))} data-testid="input-make" /></div>
              <div><Label>Model</Label><Input value={form.vehicle_model || ''} onChange={e => setForm(f => ({...f, vehicle_model: e.target.value}))} data-testid="input-model" /></div>
              <div>
                <Label>Body Type</Label>
                <select
                  value={form.vehicle_body_type || ''}
                  onChange={e => setForm(f => ({...f, vehicle_body_type: e.target.value}))}
                  className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                  data-testid="select-vehicle-body-type"
                >
                  <option value="">Select type...</option>
                  {VEHICLE_BODY_TYPES.map(g => (
                    <optgroup key={g.group} label={g.group}>
                      {g.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                    </optgroup>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Addresses with Zip */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900">Pickup & Delivery</h3>
              {(form.estimated_distance || quote?.estimated_distance) ? (
                <span className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold" data-testid="quote-distance-badge">
                  {(form.estimated_distance || quote?.estimated_distance).toLocaleString()} miles
                </span>
              ) : null}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-blue-600">Pickup</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={form.pickup_city || ''} onChange={e => setForm(f => ({...f, pickup_city: e.target.value}))} data-testid="input-pickup-city" /></div>
                  <div><Label>State</Label><Input value={form.pickup_state || ''} onChange={e => setForm(f => ({...f, pickup_state: e.target.value}))} data-testid="input-pickup-state" /></div>
                  <div><Label>Zip Code</Label><Input value={form.pickup_zip || ''} onChange={e => setForm(f => ({...f, pickup_zip: e.target.value}))} placeholder="e.g. 90001" data-testid="input-pickup-zip" /></div>
                </div>
              </div>
              <div className="space-y-3">
                <h4 className="text-sm font-medium text-emerald-600">Delivery</h4>
                <div className="grid grid-cols-3 gap-3">
                  <div><Label>City</Label><Input value={form.delivery_city || ''} onChange={e => setForm(f => ({...f, delivery_city: e.target.value}))} data-testid="input-delivery-city" /></div>
                  <div><Label>State</Label><Input value={form.delivery_state || ''} onChange={e => setForm(f => ({...f, delivery_state: e.target.value}))} data-testid="input-delivery-state" /></div>
                  <div><Label>Zip Code</Label><Input value={form.delivery_zip || ''} onChange={e => setForm(f => ({...f, delivery_zip: e.target.value}))} placeholder="e.g. 77001" data-testid="input-delivery-zip" /></div>
                </div>
              </div>
            </div>
          </div>

          {/* Pricing - All 3 Types Editable */}
          <div className="bg-white rounded-xl border border-slate-200 p-5">
            <h3 className="font-semibold text-slate-900 mb-4">Pricing — All Shipping Types</h3>
            <PricingEditor
              pricing={pricing}
              distance={form.estimated_distance || quote?.estimated_distance}
              onChange={handlePricingChange}
            />
          </div>

          {/* USA Route Map */}
          {!isNew && (form.pickup_state || form.delivery_state) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quote-route-map">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Map className="w-5 h-5 text-slate-600" /></div>
                <h3 className="font-semibold text-slate-900">Transport Route</h3>
              </div>
              <USARouteMap pickupState={form.pickup_state} deliveryState={form.delivery_state} pickupCity={form.pickup_city} deliveryCity={form.delivery_city} />
            </div>
          )}

          {/* Transport Facts */}
          {!isNew && (form.pickup_state || form.delivery_state) && (
            <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quote-transport-facts">
              <h3 className="font-semibold text-slate-900 mb-4">Route Intelligence</h3>
              <TransportFacts pickupState={form.pickup_state} deliveryState={form.delivery_state} pickupCity={form.pickup_city} deliveryCity={form.delivery_city} />
            </div>
          )}

          {/* Notes */}
          <div className="bg-white rounded-xl border border-slate-200 p-5" data-testid="quote-notes-section">
            <h3 className="font-semibold text-slate-900 mb-4">Notes</h3>
            <div className="flex gap-2 mb-4">
              <textarea className="flex-1 rounded-lg border border-slate-200 p-3 text-sm min-h-[60px] focus:outline-none focus:ring-2 focus:ring-blue-500"
                value={newNote} onChange={e => setNewNote(e.target.value)} placeholder="Add a new note..." data-testid="new-note-input" />
              <Button className="bg-blue-600 hover:bg-blue-700 text-white self-end" onClick={handleAddNote} disabled={!newNote.trim()} data-testid="add-note-btn">Add Note</Button>
            </div>
            {notes.length > 0 ? (
              <div className="space-y-3 max-h-64 overflow-y-auto">
                {[...notes].reverse().map(note => (
                  <div key={note.id} className="bg-slate-50 rounded-lg p-3 border border-slate-100" data-testid={`note-${note.id}`}>
                    <p className="text-sm text-slate-800 whitespace-pre-wrap">{note.text}</p>
                    <div className="flex items-center gap-2 mt-2 text-xs text-slate-400">
                      <span className="font-medium text-slate-500">{note.user_name}</span>
                      <span>&bull;</span>
                      <span>{new Date(note.created_at).toLocaleDateString()}</span>
                      <span>{new Date(note.created_at).toLocaleTimeString([], {hour: '2-digit', minute: '2-digit'})}</span>
                    </div>
                  </div>
                ))}
              </div>
            ) : <p className="text-sm text-slate-400 italic">No notes yet</p>}
          </div>
        </div>
      </div>

      <ConfirmDialog
        open={showConvertConfirm}
        title="Convert to Order"
        message="Convert this quote to an order? You'll be redirected to review and edit all order details."
        confirmText="Convert to Order"
        variant="convert"
        onConfirm={doConvert}
        onCancel={() => setShowConvertConfirm(false)}
      />
    </Layout>
  );
};

export default QuoteDetail;